import { z } from 'zod'

import { MemberRole, MemberRoles } from './organization-invite'
import { PayPeriodTypeEnum } from './pay-period'
import { PermissionSetAssignmentSchema } from './permissions'

// Member status enum
export enum MemberStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  DRAFT = 'DRAFT',
  SUSPENDED = 'SUSPENDED',
}

// Member status colors
export const MEMBER_STATUS_COLORS: Record<MemberStatusEnum, string> = {
  [MemberStatusEnum.ACTIVE]: '#1ead5b',
  [MemberStatusEnum.INACTIVE]: '#6b7280',
  [MemberStatusEnum.PENDING]: '#f59e0b',
  [MemberStatusEnum.DRAFT]: '#64748b',
  [MemberStatusEnum.SUSPENDED]: '#ef4444',
}

// Member status display names
export const MEMBER_STATUS_DISPLAY_NAMES: Record<MemberStatusEnum, string> = {
  [MemberStatusEnum.ACTIVE]: 'Active',
  [MemberStatusEnum.INACTIVE]: 'Inactive',
  [MemberStatusEnum.PENDING]: 'Pending',
  [MemberStatusEnum.DRAFT]: 'Draft',
  [MemberStatusEnum.SUSPENDED]: 'Suspended',
}

// Member employment type enum
export enum MemberEmploymentTypeEnum {
  FULL_TIME = 'FULL_TIME',
  PART_TIME = 'PART_TIME',
  CONTRACTOR = 'CONTRACTOR',
  FREELANCER = 'FREELANCER',
}

// How a contractor or freelancer is paid. "Billable" only means billed to a
// client; whether non-billable time is paid is this separate decision.
//  - ALL_HOURS: every approved hour at the pay rate (non-billable time is the
//    company's internal cost).
//  - BILLABLE_HOURS: billable hours only (a blended rate covers indirect work);
//    non-billable time is recorded but unpaid.
//  - RETAINER: a fixed amount per pay period; hours are tracked for visibility.
// Employees are always paid for all hours (see MemberCompensation).
export enum ContractorPayBasisEnum {
  ALL_HOURS = 'ALL_HOURS',
  BILLABLE_HOURS = 'BILLABLE_HOURS',
  RETAINER = 'RETAINER',
}

// Member sort field enum
export enum MemberSortFieldEnum {
  NAME = 'name',
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  STATUS = 'status',
  DEPARTMENT = 'department',
  JOB_TITLE = 'jobTitle',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',
}

// Member widget promotion settings
export interface MemberWidgetPromotionSettings {
  dismissedWidgetPromotions?: string[] // Array of widget IDs that were dismissed
}

// User schema for project member (linked user account)
export const MemberUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  image: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  displayName: z.string().nullable(),
  timezone: z.string().nullable().optional(),
})

// Member permissions schema
export const MemberPermissionsSchema = z.object({
  permissionSets: z.array(PermissionSetAssignmentSchema),
})

export const MemberDepartmentSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
})

export const MemberTeamMembershipSchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  memberId: z.string().uuid(),
  isPrimary: z.boolean(),
  team: MemberDepartmentSummarySchema.optional(),
})

// Member schema with direct fields
export const MemberSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid().nullable().optional(),
  status: z.string().default(MemberStatusEnum.ACTIVE),
  role: z.enum(MemberRoles).default('MEMBER'),
  createdAt: z.date(),
  updatedAt: z.date(),
  settings: z.any().default({}),

  // Standard member fields (organization-specific, copied from user on creation)
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  image: z.string().nullable().optional(),

  // Professional information
  jobTitle: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  departmentTeamId: z.string().uuid().nullable().optional(),
  primaryTeam: MemberDepartmentSummarySchema.nullable().optional(),
  teamMemberships: z.array(MemberTeamMembershipSchema).optional(),
  employmentType: z.string().default(MemberEmploymentTypeEnum.FULL_TIME),

  // Capacity & scheduling
  weeklyCapacity: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  targetUtilization: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  targetBillablePercent: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),

  // Contractor pay basis override (null = use the org default) and, for
  // RETAINER, the amount paid per pay period.
  contractorPayBasis: z
    .nativeEnum(ContractorPayBasisEnum)
    .nullable()
    .optional(),
  contractorRetainerAmount: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  contractorRetainerCurrency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/)
    .nullable()
    .optional(),

  // Pay period override (null = use org default)
  payPeriodType: z.nativeEnum(PayPeriodTypeEnum).nullable().optional(),
  payPeriodStartDay: z.number().nullable().optional(),
  firstPayPeriodDate: z.string().nullable().optional(),
  semiMonthlyFirstDay: z.number().nullable().optional(),
  semiMonthlySecondDay: z.number().nullable().optional(),

  // Reporting structure
  reportsToId: z.string().uuid().nullable().optional(),
  reportsTo: z
    .object({
      id: z.string().uuid(),
      firstName: z.string().nullable(),
      lastName: z.string().nullable(),
      image: z.string().nullable(),
    })
    .nullable()
    .optional(),

  // Linked user account (for reference)
  user: MemberUserSchema.nullable().optional(),
  permissions: MemberPermissionsSchema.optional(),
})

export type Member = z.infer<typeof MemberSchema>

// Update Member schema - all editable member fields
export const UpdateMemberSchema = z.object({
  status: z.string().optional(),
  role: z.enum(MemberRoles).optional(),
  settings: z.any().optional(),

  // Standard member fields
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  image: z.string().nullable().optional(),

  // Professional information
  jobTitle: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  departmentTeamId: z.string().uuid().nullable().optional(),
  employmentType: z.string().optional(),

  // Capacity & scheduling
  weeklyCapacity: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  targetUtilization: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  targetBillablePercent: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),

  // Contractor pay basis override (null = use the org default) and, for
  // RETAINER, the amount paid per pay period.
  contractorPayBasis: z
    .nativeEnum(ContractorPayBasisEnum)
    .nullable()
    .optional(),
  contractorRetainerAmount: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  contractorRetainerCurrency: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/)
    .nullable()
    .optional(),

  // Pay period override (null = use org default)
  payPeriodType: z.nativeEnum(PayPeriodTypeEnum).nullable().optional(),
  payPeriodStartDay: z.number().nullable().optional(),
  firstPayPeriodDate: z.string().nullable().optional(),
  semiMonthlyFirstDay: z.number().nullable().optional(),
  semiMonthlySecondDay: z.number().nullable().optional(),

  // Controls how a mid-period type change is applied
  payPeriodSwitchMode: z.enum(['immediate', 'end_of_period']).optional(),

  // Reporting structure
  reportsToId: z.string().uuid().nullable().optional(),
})

export type UpdateMember = z.infer<typeof UpdateMemberSchema>

export const CreateMemberSchema = z.object({
  firstName: z.string().trim().min(1).nullable().optional(),
  lastName: z.string().trim().min(1).nullable().optional(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  image: z.string().nullable().optional(),
  role: z.enum(MemberRoles).default('MEMBER'),
  status: z.string().default(MemberStatusEnum.DRAFT),
  settings: z.any().optional(),
  jobTitle: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  departmentTeamId: z.string().uuid().nullable().optional(),
  employmentType: z.string().optional(),
  weeklyCapacity: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  targetUtilization: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? parseFloat(val) : val))
    .nullable()
    .optional(),
  reportsToId: z.string().uuid().nullable().optional(),
})

export type CreateMember = z.infer<typeof CreateMemberSchema>

// Member response schema
export const MemberResponseSchema = z.object({
  data: MemberSchema,
  message: z.string().optional(),
})

export type MemberResponse = z.infer<typeof MemberResponseSchema>

// Members response schema
export const MembersResponseSchema = z.object({
  data: z.array(MemberSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type MembersResponse = z.infer<typeof MembersResponseSchema>
